/**
 * Filters for partners.
 */
import * as React from 'react';
import { SearchInput } from '@/components/forms/SearchInput';
import type { PartnerFilters } from './partner-configs';

/** Props for {@link PartnerFiltersContent}. */
interface PartnerFiltersContentProps {
  filters: PartnerFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onSetSearch: (v: string | undefined) => void;
  onSetCity: (v: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(filterId: string, allowed: string[], visible: string[]): boolean {
  return allowed.includes(filterId) && visible.includes(filterId);
}

/** Free-text search across partner fields. */
const SearchFilter: React.FC<{
  filters: PartnerFilters;
  onSetSearch: (v: string | undefined) => void;
}> = ({ filters, onSetSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-56">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Rechercher un partenaire..."
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Free-text city filter. */
const CityFilter: React.FC<{
  filters: PartnerFilters;
  onSetCity: (v: string | undefined) => void;
}> = ({ filters, onSetCity }) => (
  <div className="filter-group">
    <span className="filter-group-label">Ville</span>
    <div className="w-48">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Filtrer par ville..."
        value={filters.city ?? ''}
        onSearch={(q) => onSetCity(q.trim() || undefined)}
        onClear={() => onSetCity(undefined)}
      />
    </div>
  </div>
);

/** Ribbon of partner filters; each group is gated by RBAC + configure-panel visibility. */
export const PartnerFiltersContent: React.FC<PartnerFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onSetSearch,
  onSetCity,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-search-partner') && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} />
      )}
      {show('filter-city-partner') && <CityFilter filters={filters} onSetCity={onSetCity} />}
    </div>
  );
};
